// FeedbackDetail.js - Admin detail panel for feedback item
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';
import api from '../../utils/api';

// Get the file URL - in development, proxy doesn't work for new tabs, so use direct backend URL
const getFileUrl = (filePath) => {
  const filename = filePath.split('/').pop().split('\\').pop();
  const token = localStorage.getItem('gchain_token');
  // In development, we need to hit the backend directly since proxy doesn't work for new tabs
  const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';
  return `${baseUrl}/api/feedback/file/${filename}?token=${token}`;
};

// Get just the filename from a path
const getFilename = (filePath) => {
  return filePath.split('/').pop().split('\\').pop();
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${THEME.spacing.md};
`;

const TypeIcon = styled.span`
  font-size: ${THEME.fontSize.xxl};
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0 0 ${THEME.spacing.xs} 0;
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.lg};
`;

const Meta = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.sm};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.xs};
`;

const SectionLabel = styled.label`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xs};
  text-transform: uppercase;
  font-weight: 500;
`;

const SectionText = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  line-height: 1.5;
  white-space: pre-wrap;
  background: ${THEME.inputBackground};
  padding: ${THEME.spacing.md};
  border-radius: ${THEME.borderRadiusSmall};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.xs};
`;

const Select = styled.select`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }
`;

const TextArea = styled.textarea`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const Input = styled.input`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const Row = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
`;

const HalfWidth = styled.div`
  flex: 1;
`;

const SaveButton = styled.button`
  padding: ${THEME.spacing.md} ${THEME.spacing.lg};
  background: ${THEME.buttonPrimary};
  color: white;
  border: none;
  border-radius: ${THEME.borderRadiusMedium};
  font-size: ${THEME.fontSize.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover:not(:disabled) {
    background: ${THEME.buttonHover};
  }

  &:disabled {
    background: ${THEME.buttonDisabled};
    cursor: not-allowed;
  }
`;

const AttachmentList = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
`;

const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.xs};
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  background: ${THEME.buttonSecondary};
  color: ${THEME.textPrimary};
  text-decoration: none;
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};

  &:hover {
    background: ${THEME.inputBorder};
  }
`;

const AttachmentButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.xs};
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  background: ${THEME.buttonSecondary};
  color: ${THEME.textPrimary};
  border: none;
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${THEME.inputBorder};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  color: ${THEME.success};
  font-size: ${THEME.fontSize.sm};
  padding: ${THEME.spacing.sm};
  background: rgba(76, 175, 80, 0.1);
  border-radius: ${THEME.borderRadiusSmall};
`;

const ErrorMessage = styled.div`
  color: ${THEME.error};
  font-size: ${THEME.fontSize.sm};
  padding: ${THEME.spacing.sm};
  background: rgba(255, 165, 0, 0.1);
  border-radius: ${THEME.borderRadiusSmall};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${THEME.inputBorder};
  margin: ${THEME.spacing.md} 0;
`;

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FeedbackDetail = ({ item, onUpdate, onLoadProject }) => {
  const [status, setStatus] = useState(item.status);
  const [priority, setPriority] = useState(item.priority);
  const [adminNotes, setAdminNotes] = useState(item.admin_notes || '');
  const [adminResponse, setAdminResponse] = useState(item.admin_response || '');
  const [completionMessage, setCompletionMessage] = useState(item.completion_message || '');
  const [saving, setSaving] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when item changes
  useEffect(() => {
    setStatus(item.status);
    setPriority(item.priority);
    setAdminNotes(item.admin_notes || '');
    setAdminResponse(item.admin_response || '');
    setCompletionMessage(item.completion_message || '');
    setSuccess(false);
    setError(null);
  }, [item.id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await onUpdate(item.id, {
        status,
        priority,
        admin_notes: adminNotes || null,
        admin_response: adminResponse || null,
        completion_message: completionMessage || null
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadProject = async () => {
    if (!item.project_json_path || !onLoadProject) return;

    setLoadingProject(true);
    setError(null);

    try {
      const filename = getFilename(item.project_json_path);
      const projectData = await api.getFeedbackFile(filename);
      await onLoadProject(projectData);
    } catch (err) {
      setError(err.message || 'Failed to load project');
    } finally {
      setLoadingProject(false);
    }
  };

  const hasChanges =
    status !== item.status ||
    priority !== item.priority ||
    adminNotes !== (item.admin_notes || '') ||
    adminResponse !== (item.admin_response || '') ||
    completionMessage !== (item.completion_message || '');

  return (
    <Container>
      <Header>
        <TypeIcon>
          {item.type === 'bug' ? '🐛' : '✨'}
        </TypeIcon>
        <HeaderContent>
          <Title>{item.title}</Title>
          <Meta>
            <span>@{item.username}</span>
            <span>{item.user_email}</span>
            <span>{formatDate(item.created_at)}</span>
          </Meta>
        </HeaderContent>
      </Header>

      <Section>
        <SectionLabel>Description</SectionLabel>
        <SectionText>{item.description}</SectionText>
      </Section>

      {item.area && (
        <Section>
          <SectionLabel>Area</SectionLabel>
          <SectionText>{item.area}</SectionText>
        </Section>
      )}

      {(item.canvas_image_path || item.project_json_path) && (
        <Section>
          <SectionLabel>Attachments</SectionLabel>
          <AttachmentList>
            {item.canvas_image_path && (
              <AttachmentLink
                href={getFileUrl(item.canvas_image_path)}
                target="_blank"
                rel="noopener noreferrer"
              >
                📷 Canvas Image
              </AttachmentLink>
            )}
            {item.project_json_path && (
              <AttachmentButton
                onClick={handleLoadProject}
                disabled={loadingProject}
                title="Load this project into the canvas"
              >
                {loadingProject ? '⏳ Loading...' : '📁 Load Project'}
              </AttachmentButton>
            )}
          </AttachmentList>
        </Section>
      )}

      <Divider />

      <Row>
        <HalfWidth>
          <FormGroup>
            <SectionLabel>Status</SectionLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="submitted">Submitted</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="wont_fix">Won't Fix</option>
            </Select>
          </FormGroup>
        </HalfWidth>
        <HalfWidth>
          <FormGroup>
            <SectionLabel>Priority</SectionLabel>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          </FormGroup>
        </HalfWidth>
      </Row>

      <FormGroup>
        <SectionLabel>Admin Notes (internal, not visible to user)</SectionLabel>
        <TextArea
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Internal notes about this item..."
        />
      </FormGroup>

      <FormGroup>
        <SectionLabel>Response to User (visible when they view their request)</SectionLabel>
        <TextArea
          value={adminResponse}
          onChange={(e) => setAdminResponse(e.target.value)}
          placeholder="Your response to the user..."
        />
      </FormGroup>

      {(status === 'completed' || status === 'wont_fix') && (
        <FormGroup>
          <SectionLabel>
            Completion Message (shown in Recent Updates - keep it brief)
          </SectionLabel>
          <Input
            type="text"
            value={completionMessage}
            onChange={(e) => setCompletionMessage(e.target.value)}
            placeholder="e.g., Smith Chart display issue resolved"
            maxLength={500}
          />
        </FormGroup>
      )}

      {success && <SuccessMessage>Changes saved successfully!</SuccessMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SaveButton
        onClick={handleSave}
        disabled={saving || !hasChanges}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </SaveButton>

      {item.completed_at && (
        <Meta style={{ justifyContent: 'center' }}>
          Completed: {formatDate(item.completed_at)}
        </Meta>
      )}
    </Container>
  );
};

export default FeedbackDetail;
